import { object } from 'prop-types';
import { useState, useEffect, useMemo } from 'react';

import { HeaderSearch, FormCard, Row, Col } from 'twyne-new.ui';

import { useAppAlert } from '@Components/AppAlert';
import { useGlobalStore } from '@Stores/global';

import { useSettingStore } from '../../store';

import ThemeItem from './ThemeItem';

export default function ThemeSelectorForm({ themes }) {
  const { siteSettings: { item: { theme } } } = useGlobalStore()
  const { syncTheme, useSubmit } = useSettingStore()
  const { handleSuccess, handleError } = useAppAlert()
  const [searchFocused, setSearchFocused] = useState(false)

  const [selectedTheme, setSelectedTheme] = useState(DEFAULT_THEME)
  const [filterText, setFilterText] = useState('')

  const themesList = useMemo(() => Object.entries({ default: DEFAULT_THEME, ...themes }), [themes])
  const availableThemes = useMemo(() => themesList.filter(([key]) => key !== theme), [theme, themesList])

  const showingThemes = useMemo(() => {
    const regex = new RegExp(filterText, 'i')
    return availableThemes.filter(([key, { name, description }]) => {
      if (key !== theme) {
        return regex.test(key) || regex.test(name) || regex.test(description)
      }
      return false
    })
  }, [filterText, theme, availableThemes])

  useEffect(() => {
    if (theme) {
      const [, selected] = themesList.find(([key]) => key === theme) || []
      selected && setSelectedTheme(selected)
    }
  }, [theme, themesList])

  const handleSubmit = useSubmit()

  const handleThemeSelect = async theme => {
    try {
      await handleSubmit({ theme })
      await syncTheme()
      handleSuccess('Success', 'Theme selected.')
    } catch (error) {
      handleError('Error', 'Theme was not selected.')
    }
  }

  return <>
    <Row>
      <Col>
        <FormCard title='Current theme'>
          <ThemeItem name={theme} theme={selectedTheme} active />
        </FormCard>
      </Col>
      <Col>
        <FormCard title='Available themes' headerButtons={<HeaderSearch
          focused={searchFocused || !!filterText}
          onFocus={setSearchFocused}
          value={filterText}
          onChange={setFilterText}
          className={!(searchFocused || !!filterText) && 'w-[6rem]!'}
        />}>
          {showingThemes?.length ? <>
            {showingThemes.map(([key, theme], index) => <ThemeItem
              key={`${key}-${index}`}
              name={key}
              theme={theme}
              onClick={handleThemeSelect}
            />)}
          </> : <div className='text-center py-4'>No themes found</div>}
        </FormCard>
      </Col>
    </Row>
  </>
}

ThemeSelectorForm.propTypes = {
  themes: object,
}

ThemeSelectorForm.defaultProps = {}

const DEFAULT_THEME = {}
